import { useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface FaqItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: "Is Midwest Fear Fest appropriate for children?",
    answer:
      "No. Our festival programs independent genre short films that are intended for mature audiences. Films are not rated, but content may include horror themes, violence, and adult subject matter.",
  },
  {
    question: "What is the refund policy?",
    answer:
      "Ticketing is handled by Kan-Kan Cinema and all sales are final. In the unlikely event of a cancellation, we will communicate directly with attendees to determine next steps.",
  },
  {
    question: "Is parking available?",
    answer:
      "Free parking is available in the adjacent lot at Kan-Kan Cinema & Brasserie. Additional street parking is available throughout the Windsor Park neighborhood. We recommend arriving early as the lot fills quickly on Friday and Saturday nights. Rideshare drop-off is available at the Windsor St entrance.",
  },
  {
    question: "Can I buy tickets at the door?",
    answer:
      "Yes, if tickets are still available. We recommend purchasing in advance, as screenings have sold out prior to the event in previous years.",
  },
  {
    question: "What should I wear?",
    answer:
      "Come as you are or have fun with it! Dressing up is welcome but never required. For safety reasons, masks and prop weapons are not permitted.",
  },
  {
    question: "How are ticket, donation, and merchandise funds used?",
    answer:
      "As a volunteer-run nonprofit, all revenue goes directly back into the festival. Funds support event operations and filmmaker awards, including trophies and cash prizes.",
  },
  {
    question: "Will the featured filmmakers be in attendance?",
    answer:
      "We invite filmmakers to attend and engage with audiences; however, attendance is subject to availability and cannot be guaranteed.",
  },
  {
    question: "How long is the event?",
    answer:
      "Midwest Fear Fest typically runs about 2 hours, though timing may vary slightly by year. The event opens with an introduction and filmmaker acknowledgements, followed by a screening of selected short films. The program concludes with award presentations and an audience vote for favorite short film.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const ref = useScrollAnimation();

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      id="faq"
      style={{
        background: "#0f0f14",
        padding: "clamp(64px,10vw,120px) clamp(20px,5vw,80px)",
      }}
    >
      <div ref={ref} style={{ maxWidth: "860px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "56px", textAlign: "center" }}>
          <div
            className="section-label animate-on-scroll"
            style={{ justifyContent: "center", marginBottom: "16px" }}
          >
            Got Questions?
          </div>
          <h2
            className="display-heading animate-on-scroll"
            style={{
              fontSize: "clamp(2.5rem,6vw,4.5rem)",
              color: "#e8e8e8",
              transitionDelay: "100ms",
            }}
          >
            Frequently Asked <span style={{ color: "#cc0000" }}>Questions</span>
          </h2>
        </div>

        {/* Accordion */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className="animate-on-scroll"
                style={{ transitionDelay: `${index * 60}ms` }}
              >
                <div className={`faq-item${isOpen ? " open" : ""}`}>
                  <button
                    className="faq-question"
                    onClick={() => toggle(index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span>{item.question}</span>
                    <div className={`faq-icon${isOpen ? " rotated" : ""}`}>
                      +
                    </div>
                  </button>
                  <div
                    id={`faq-answer-${index}`}
                    className={`faq-answer${isOpen ? " open" : ""}`}
                    role="region"
                  >
                    <p>{item.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* More questions CTA */}
        <div
          className="animate-on-scroll"
          style={{
            textAlign: "center",
            marginTop: "48px",
            padding: "32px",
            borderRadius: "16px",
            background: "rgba(204,0,0,0.06)",
            border: "1px solid rgba(204,0,0,0.15)",
          }}
        >
          <p
            style={{
              color: "rgba(232,232,232,0.75)",
              fontSize: "0.95rem",
              marginBottom: "16px",
            }}
          >
            Still have questions? We're happy to help.
          </p>
          <a
            href="#contact"
            className="btn-primary"
            style={{ display: "inline-flex" }}
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
}
